import type { Navigation } from "zudoku";
import {
  METHODS,
  TUTORIAL_STEPS,
  buildMethods,
} from "./src/components/getting-started/data.js";
import zudokuSidebar from "./sidebar.zudoku.json";
import cliSidebar from "./sidebar.cli.json";
import policySidebars from "./sidebar.policies.json";

const apiManagementPolicies: Navigation = [
  "policies/overview",
  ...(policySidebars["api-gateway"] as Navigation),
  {
    type: "category",
    label: "Guides",
    items: [
      "articles/multiple-auth-policies",
      "rate-limiting/per-user-rate-limits-using-db",
      "articles/composite-policy-reference",
      "articles/securing-the-gateway-with-client-mtls",
      "articles/workload-identity-inbound",
    ],
  },
];

const aiGatewayPolicies: Navigation = [
  "ai-gateway/policies/overview",
  ...(policySidebars["ai-gateway"] as Navigation),
];

const mcpGatewayPolicies: Navigation = [
  "mcp-gateway/policies/overview",
  ...(policySidebars["mcp-gateway"] as Navigation),
];

const programming: Navigation = [
  {
    type: "doc",
    file: "programmable-api/overview",
    label: "Overview",
  },

  {
    type: "category",
    label: "Request & Context",
    items: ["programmable-api/zuplo-request", "programmable-api/zuplo-context"],
  },
  {
    type: "category",
    label: "Configuration",
    items: [
      "programmable-api/zuplo-json",
      "programmable-api/compatibility-dates",
      "programmable-api/environment",
    ],
  },

  {
    type: "category",
    label: "Caching APIs",
    items: [
      "programmable-api/zone-cache",
      "programmable-api/memory-zone-read-through-cache",
      "programmable-api/streaming-zone-cache",
      "programmable-api/cache",
    ],
  },
  {
    type: "category",
    label: "Data Management",
    items: [
      "programmable-api/context-data",
      "programmable-api/background-loader",
      "programmable-api/background-dispatcher",
    ],
  },
  {
    type: "category",
    label: "Extensions & Hooks",
    items: ["programmable-api/runtime-extensions", "programmable-api/hooks"],
  },
  {
    type: "category",
    label: "Error Handling",
    items: [
      "programmable-api/runtime-errors",
      "programmable-api/not-found-handler",
      "programmable-api/http-problems",
      "programmable-api/problem-response-formatter",
    ],
  },
  {
    type: "category",
    label: "Logging & Observability",
    items: ["programmable-api/logger", "programmable-api/console-logging"],
  },
  {
    type: "category",
    label: "Types and Interfaces",
    items: [
      "programmable-api/request-user",
      "programmable-api/custom-cors-policy",
    ],
  },
  {
    type: "category",
    label: "Web Standards",
    items: [
      "programmable-api/web-standard-apis",
      "programmable-api/web-crypto-apis",
    ],
  },
  {
    type: "category",
    label: "MCP",
    items: ["programmable-api/mcp-sdk", "programmable-api/mcp-gateway-plugin"],
  },
  {
    type: "category",
    label: "Advanced Topics",
    items: [
      "programmable-api/node-modules",
      "programmable-api/reusing-code",
      "programmable-api/route-raw",
      "programmable-api/safely-clone-a-request-or-response",
      "programmable-api/runtime-behaviors",
      "programmable-api/zp-body-removed",
      "programmable-api/zuplo-id-token",
      "programmable-api/jwt-service-plugin",
      "programmable-api/oauth-protected-resource-plugin",
    ],
  },
];

// A product area's Getting Started: a row that opens that product's method
// picker, plus the three methods listed hidden beside it.
//
// It is a category with no items rather than a plain doc entry because the two
// highlight differently: Zudoku renders a doc row with `end`, so it only lights
// up on an exact URL match, while a category's link matches by prefix. Only the
// category keeps the row marked as the current section while a reader is inside
// `/getting-started/<build>/<method>`. `collapsible: false` and the empty
// `items` are what stop it rendering a disclosure arrow that opens onto
// nothing — the methods are deliberately not shown, so there is nothing to
// expand.
//
// The methods still have to appear somewhere in the tree: Zudoku resolves a
// page's sidebar from the navigation data, and a page in no entry renders with
// no sidebar at all. Hidden siblings give them that context without adding
// rows.
//
// Each product area owns its own onboarding, so these live inside the product
// sections below rather than in one shared Getting Started tree. Keep the
// method ids in sync with METHODS in src/components/getting-started/data.ts.
const gettingStarted = (build: string): Navigation => {
  const methods = buildMethods(build);
  if (methods.length === 1) {
    return [
      {
        type: "doc",
        label: "Getting Started",
        file: `getting-started/${build}/${methods[0]!.id}`,
      },
    ];
  }
  // A build whose tutorials are split across pages lists the steps themselves,
  // and nothing else — the method switcher on the page moves between the
  // Portal, CLI and agent versions of whichever step you are on.
  //
  // Each step is a category with a link and no items, the same shape the
  // Getting Started row itself uses. A category's link matches by prefix, so
  // the row stays marked on /<build>/<slug> and /<build>/<slug>/<method>
  // alike; a plain doc row could not, because Zudoku renders those with `end`
  // and they only match exactly.
  //
  // The other methods still have to appear somewhere — a page in no entry
  // renders with no sidebar at all — so they follow the steps as hidden rows.
  // They sit after the whole list rather than one inside each step because
  // the pager walks the tree in order and does not skip hidden pages: nested,
  // Next on "2 - Add rate limiting" led to the same step in the other method
  // instead of step 3. Listed in method order they read as two sequences, so
  // Next moves down whichever one you are already reading.
  const defaultMethod = METHODS[0]!.id;
  const listedSteps = TUTORIAL_STEPS[`${build}:${defaultMethod}`];

  if (listedSteps) {
    const otherMethods = methods.filter((m) => m.id !== defaultMethod);

    const stepRows: Navigation = listedSteps.map((step, i) => ({
      type: "category",
      label: `${i + 1} - ${step.title}`,
      collapsible: false,
      link: {
        type: "doc",
        file: `getting-started/${build}/${step.slug}/index`,
        path: `getting-started/${build}/${step.slug}`,
      },
      items: [],
    }));

    const otherMethodRows: Navigation = otherMethods.flatMap((method) => {
      const steps = TUTORIAL_STEPS[`${build}:${method.id}`];
      // A method whose tutorial is a single page has no steps to list.
      const files = steps
        ? steps.map(
            (step) => `getting-started/${build}/${step.slug}/${method.id}`,
          )
        : [`getting-started/${build}/${method.id}`];
      return files.map((file) => ({ type: "doc", file, display: "hide" }));
    });

    return [
      {
        type: "category",
        label: "Getting Started",
        link: {
          type: "doc",
          file: `getting-started/${build}/index`,
          path: `getting-started/${build}`,
        },
        items: [...stepRows, ...otherMethodRows],
      },
    ];
  }

  return [
    {
      type: "category",
      label: "Getting Started",
      collapsible: false,
      link: {
        type: "doc",
        file: `getting-started/${build}/index`,
        path: `getting-started/${build}`,
      },
      items: [],
    },
    ...methods.map(
      (method) =>
        ({
          type: "doc",
          file: `getting-started/${build}/${method.id}`,
          display: "hide",
        }) as const,
    ),
  ];
};

const devPortalItems: Navigation = [
  "dev-portal/overview",
  ...gettingStarted("dev-portal"),
  "dev-portal/updating",
  "dev-portal/node-modules",
  "dev-portal/branding",
  ...(zudokuSidebar as Navigation).map((item) => {
    if (typeof item === "object" && item.type === "category") {
      if (item.label === "Guides") {
        return {
          ...item,
          items: [
            ...item.items,
            "dev-portal/auth-provider-api-identities",
            "dev-portal/dev-portal-create-consumer-on-auth",
            "dev-portal/documenting-mcp-servers",
            "dev-portal/server-side-rendering",
            "articles/rick-and-morty-api-developer-portal-example",
          ],
        };
      }
    }
    return item;
  }),
];

export const documentation: Navigation = [
  {
    type: "filter",
    placeholder: "Filter documentation",
  },
  {
    type: "doc",
    file: "home",
    path: "/",
    label: "Home",
    display: "hide",
  },
  // The wizard's entry point — pick what to build, then how. Everything it
  // leads to is listed under the matching product area below, so nothing is
  // nested here.
  {
    type: "doc",
    file: "getting-started/index",
    path: "getting-started",
    label: "Getting Started",
    icon: "rocket",
  },
  {
    type: "category",
    label: "Concepts",
    icon: "lightbulb",
    items: [
      "concepts/how-zuplo-works",
      "concepts/where-zuplo-runs",
      "concepts/request-lifecycle",
      "concepts/project-structure",
      "concepts/authentication",
      "concepts/upstream-credentials",
      "concepts/api-keys",
      "concepts/api-errors",
      "articles/routing",
      "articles/policies",
      "articles/openapi",
      "articles/environments",
      "concepts/source-control-and-deployment",
      "articles/development-options",
      "concepts/ai-gateway",
      "concepts/mcp",
    ],
  },
  // The five product areas.
  {
    type: "category",
    label: "API Management",
    icon: "layers",
    items: [
      "api-management/overview",
      ...gettingStarted("gateway"),
      {
        type: "category",
        label: "API Keys",
        items: [
          "articles/api-key-management",
          "articles/when-to-use-api-keys",
          "articles/api-key-best-practices",
          "articles/api-key-authentication",
          "articles/api-key-metadata",
          "articles/api-key-administration",
          "articles/api-key-consumer-bucket-portal-ui",
          "articles/api-key-end-users",
          "articles/api-key-api",
          "articles/api-key-self-serve-integration",
          "articles/api-key-buckets",
          "articles/api-key-leak-detection",
          "articles/api-key-service-limits",
        ],
      },
      {
        type: "category",
        label: "Rate Limiting",
        items: [
          "rate-limiting/getting-started",
          "rate-limiting/how-it-works",
          {
            type: "category",
            label: "Policies",
            items: [
              "policies/rate-limit-inbound",
              "policies/complex-rate-limit-inbound",
            ],
          },
          {
            type: "category",
            label: "Guides",
            items: [
              "rate-limiting/dynamic-rate-limiting",
              "rate-limiting/combining-policies",
              "rate-limiting/per-user-rate-limits-using-db",
              "rate-limiting/monitoring-and-troubleshooting",
            ],
          },
        ],
      },
      {
        type: "category",
        label: "Caching",
        items: [
          "caching/overview",
          "caching/cdn-caching",
          "caching/gateway-caching",
          {
            type: "category",
            label: "Policies",
            items: [
              "policies/cdn-cache-control-outbound",
              "policies/caching-inbound",
            ],
          },
          {
            type: "category",
            label: "Guides",
            items: [
              "caching/dynamic-cache-rules",
              "caching/partial-response-caching",
              "caching/custom-caching-policy",
            ],
          },
        ],
      },
      {
        type: "category",
        label: "GraphQL",
        items: [
          {
            type: "doc",
            file: "articles/graphql-support",
            label: "Overview",
          },
          {
            type: "doc",
            file: "articles/graphql",
            label: "Set up an endpoint",
          },
          "articles/graphql-security",
          "articles/graphql-caching",
        ],
      },
      {
        type: "category",
        label: "Monetization",
        items: [
          {
            type: "doc",
            file: "articles/monetization/index",
            path: "articles/monetization",
          },
          "articles/monetization/quickstart",
          {
            type: "category",
            label: "Concepts",
            items: [
              "articles/monetization/meters",
              "articles/monetization/features",
              "articles/monetization/plans",
              "articles/monetization/rate-cards",
              "articles/monetization/pricing-models",
              "articles/monetization/billing-models",
            ],
          },
          {
            type: "category",
            label: "Guides",
            items: [
              "articles/monetization/stripe-integration",
              "articles/monetization/developer-portal",
              "articles/monetization/monetization-policy",
              "articles/monetization/subscription-data",
              "articles/monetization/dynamic-metering",
              "articles/monetization/programmatic-monetization",
              "articles/monetization/subscription-lifecycle",
              "articles/monetization/private-plans",
              "articles/monetization/tax-collection",
              "articles/monetization/going-to-production",
            ],
          },
          {
            type: "category",
            label: "Reference",
            items: [
              "articles/monetization/api-access",
              "articles/monetization/plan-examples",
            ],
          },
          "articles/monetization/troubleshooting",
          "articles/monetization-integrations",
          "articles/monetization-custom",
        ],
      },
      {
        type: "category",
        label: "Policies",
        items: apiManagementPolicies,
      },
      {
        type: "category",
        label: "Handlers",
        items: [
          "handlers/url-forward",
          "handlers/url-rewrite",
          "handlers/custom-handler",
          "handlers/aws-lambda",
          "handlers/mcp-server",
          "handlers/redirect",
          "handlers/openapi",
          "handlers/system-handlers",
          "handlers/websocket-handler",
          "handlers/websocket-pipeline-handler",
        ],
      },
    ],
  },
  {
    type: "category",
    label: "AI Gateway",
    icon: "brain",
    items: [
      "ai-gateway/overview",
      ...gettingStarted("ai-gateway"),
      "ai-gateway/source-control",
      "ai-gateway/universal-api",
      {
        type: "category",
        label: "Providers",
        items: [
          "ai-gateway/providers",
          "ai-gateway/managing-providers",
          "ai-gateway/custom-pricing",
          "ai-gateway/azure-ai",
          "ai-gateway/bedrock-mantle",
          "ai-gateway/bedrock-runtime",
          "ai-gateway/vertex-ai",
          "ai-gateway/openrouter",
          "ai-gateway/jev",
          "ai-gateway/custom-providers",
        ],
      },
      {
        type: "category",
        label: "Pools",
        items: [
          "ai-gateway/pools",
          "ai-gateway/managing-pools",
          "ai-gateway/policy-templates",
        ],
      },
      {
        type: "category",
        label: "Apps",
        items: [
          "ai-gateway/apps",
          "ai-gateway/managing-apps",
          "ai-gateway/user-apps",
          "ai-gateway/policy-chains",
          "ai-gateway/custom-policies",
          "ai-gateway/fallback",
          "ai-gateway/usage-limits",
          "ai-gateway/budget-alerts",
        ],
      },
      {
        type: "category",
        label: "Policies",
        items: aiGatewayPolicies,
      },
      {
        type: "category",
        label: "Cookbooks",
        items: [
          "ai-gateway/cookbooks/dynamic-model-routing",
          "ai-gateway/cookbooks/custom-fallback",
          "ai-gateway/cookbooks/sdk-path-shim",
        ],
      },
      {
        type: "category",
        label: "Integrations",
        items: [
          "ai-gateway/integrations/ai-sdk",
          "ai-gateway/integrations/claude-code",
          "ai-gateway/integrations/claude-desktop",
          "ai-gateway/integrations/codex",
          "ai-gateway/integrations/github-copilot",
          "ai-gateway/integrations/goose",
          "ai-gateway/integrations/langchain",
          "ai-gateway/integrations/openai",
        ],
      },
    ],
  },
  {
    type: "category",
    label: "MCP Gateway",
    icon: "network",
    items: [
      "mcp-gateway/overview",
      ...gettingStarted("mcp-gateway"),
      "mcp-gateway/how-it-works",
      {
        type: "category",
        label: "Connect MCP clients",
        items: [
          "mcp-gateway/connect-clients/overview",
          "mcp-gateway/connect-clients/claude-desktop",
          "mcp-gateway/connect-clients/claude-code",
          "mcp-gateway/connect-clients/chatgpt",
          "mcp-gateway/connect-clients/cursor",
          "mcp-gateway/connect-clients/vs-code",
          "mcp-gateway/connect-clients/other-clients",
          "mcp-gateway/test-clients",
        ],
      },
      {
        type: "category",
        label: "Authentication",
        items: [
          "mcp-gateway/auth/overview",
          "mcp-gateway/auth/upstream-oauth",
          "mcp-gateway/how-to/connect-upstream-oauth",
          "mcp-gateway/how-to/connect-upstream-api-key",
          {
            type: "category",
            label: "Identity providers",
            items: [
              "mcp-gateway/auth/configuring-auth0",
              "mcp-gateway/auth/configuring-cognito",
              "mcp-gateway/auth/configuring-clerk",
              "mcp-gateway/auth/configuring-entra",
              "mcp-gateway/auth/configuring-google",
              "mcp-gateway/auth/configuring-keycloak",
              "mcp-gateway/auth/configuring-logto",
              "mcp-gateway/auth/configuring-okta",
              "mcp-gateway/auth/configuring-onelogin",
              "mcp-gateway/auth/configuring-ping",
              "mcp-gateway/auth/configuring-workos",
              "mcp-gateway/auth/configuring-generic-oidc",
            ],
          },
          "mcp-gateway/auth/manual-oauth-testing",
        ],
      },
      {
        type: "category",
        label: "Cross App Access",
        items: [
          "mcp-gateway/cross-app-access/overview",
          "mcp-gateway/cross-app-access/quickstart",
          "mcp-gateway/cross-app-access/policy-reference",
        ],
      },
      {
        type: "category",
        label: "Configuration",
        items: [
          "mcp-gateway/code-config/overview",
          "mcp-gateway/code-config/multi-upstream",
          "mcp-gateway/code-config/local-development",
          "mcp-gateway/capability-filtering",
          "mcp-gateway/how-to/curate-tools",
          "mcp-gateway/how-to/curate-tools-local",
          "mcp-gateway/code-config/mcp-proxy-handler",
          "mcp-gateway/code-config/compatibility-dates",
        ],
      },
      {
        type: "category",
        label: "Policies",
        items: mcpGatewayPolicies,
      },
      {
        type: "category",
        label: "Observability",
        items: [
          "mcp-gateway/observability/analytics",
          "mcp-gateway/observability/logging",
        ],
      },
      "mcp-gateway/reference",
      "mcp-gateway/server-registry",
      "mcp-gateway/troubleshooting",
    ],
  },
  {
    type: "category",
    label: "MCP Server",
    icon: "bot",
    items: [
      "mcp-server/overview",
      ...gettingStarted("mcp-server"),
      "mcp-server/tools",
      "mcp-server/prompts",
      "mcp-server/resources",
      "mcp-server/testing",
      "mcp-server/troubleshooting",
      "mcp-server/graphql",
      "mcp-server/custom-tools",
      "mcp-server/openai-apps-sdk",
      {
        type: "category",
        label: "Guides",
        items: [
          "articles/configuring-auth0-for-mcp-auth",
          "articles/configuring-okta-for-mcp-auth",
          "articles/manual-mcp-oauth-testing",
        ],
      },
      "mcp-server/configuration-migration-guide",
    ],
  },
  {
    type: "category",
    label: "Developer Portal",
    icon: "book-open-text",
    items: devPortalItems,
  },
  // Platform capabilities shared by every product area.
  {
    type: "category",
    label: "Development",
    icon: "wrench",
    items: [
      "articles/cors",
      "articles/environment-variables",
      "articles/branch-based-deployments",
      "articles/troubleshooting",
      "articles/terraform",
      "articles/custom-code-patterns",
      {
        type: "category",
        label: "Testing",
        link: "articles/testing",
        items: [
          "articles/testing-getting-started",
          "articles/testing-recipes",
          "articles/testing-preview-environments",
          "articles/github-deployment-testing",
        ],
      },
      {
        type: "category",
        label: "Linting",
        items: [
          "articles/api-lint",
          "articles/api-lint-configuration",
          "articles/api-lint-rules",
          "articles/api-lint-custom-rules",
          "articles/api-lint-breaking-changes",
          "articles/api-lint-ci-cd",
        ],
      },
      {
        type: "category",
        label: "Local Development",
        items: [
          "articles/local-development",
          "articles/local-development-routes-designer",
          "articles/local-development-installing-packages",
          "articles/local-development-services",
          "articles/local-development-debugging",
          "articles/local-development-env-variables",
          "articles/tsconfig",
          "articles/local-development-troubleshooting",
        ],
      },
      {
        type: "category",
        label: "Guides",
        items: [
          "articles/advanced-path-matching",
          "articles/versioning-on-zuplo",
          "articles/openapi-server-urls",
          "articles/convert-urls-to-openapi",
          "articles/use-openapi-extension-data",
          "articles/openapi-string-format-validation-warnings",
          "guides/modify-openapi-paths",
          "guides/openapi-overlays",
          "guides/canary-routing-for-employees",
          "guides/geolocation-backend-routing",
          "guides/user-based-backend-routing",
          "guides/transform-route-params-url-rewrite",
          "articles/bypass-policy-for-testing",
          "articles/testing-graphql",
          "articles/health-checks",
          "articles/performance-testing",
          "articles/troubleshooting-slow-responses",
          "articles/non-standard-ports",
          "articles/handling-form-data",
          "articles/streaming-responses-sse",
          "articles/s3-signed-url-uploads",
          "articles/check-ip-address",
          "articles/lazy-load-configuration-into-cache",
          "articles/feature-flags",
          "articles/sharing-code-across-projects",
          "articles/add-api-to-backstage",
          "articles/update-zup-in-github-action",
        ],
      },
    ],
  },
  {
    type: "category",
    label: "Deploying & Source Control",
    icon: "git-branch",
    items: [
      "articles/source-control",
      {
        type: "category",
        label: "GitHub",
        items: [
          "articles/source-control-setup-github",
          "articles/github-deployment-testing",
          {
            type: "category",
            label: "Custom CI/CD",
            link: "articles/custom-ci-cd-github",
            items: [
              "articles/ci-cd-github/basic-deployment",
              "articles/ci-cd-github/deploy-and-test",
              "articles/ci-cd-github/pr-preview-environments",
              "articles/ci-cd-github/local-testing",
              "articles/ci-cd-github/tag-based-releases",
              "articles/ci-cd-github/multi-stage-deployment",
              "articles/ci-cd-github/cleanup-on-branch-delete",
            ],
          },
        ],
      },
      {
        type: "category",
        label: "GitLab",
        link: "articles/custom-ci-cd-gitlab",
        items: [
          "articles/source-control-setup-gitlab",
          "articles/ci-cd-gitlab/basic-deployment",
          "articles/ci-cd-gitlab/deploy-and-test",
          "articles/ci-cd-gitlab/mr-preview-environments",
          "articles/ci-cd-gitlab/local-testing",
          "articles/ci-cd-gitlab/tag-based-releases",
          "articles/ci-cd-gitlab/multi-stage-deployment",
        ],
      },
      {
        type: "category",
        label: "Bitbucket",
        link: "articles/custom-ci-cd-bitbucket",
        items: [
          "articles/source-control-setup-bitbucket",
          "articles/ci-cd-bitbucket/basic-deployment",
          "articles/ci-cd-bitbucket/deploy-and-test",
          "articles/ci-cd-bitbucket/pr-preview-environments",
          "articles/ci-cd-bitbucket/local-testing",
          "articles/ci-cd-bitbucket/tag-based-releases",
          "articles/ci-cd-bitbucket/multi-stage-deployment",
        ],
      },
      {
        type: "category",
        label: "Azure DevOps",
        link: "articles/custom-ci-cd-azure",
        items: [
          "articles/source-control-setup-azure",
          "articles/ci-cd-azure/basic-deployment",
          "articles/ci-cd-azure/deploy-and-test",
          "articles/ci-cd-azure/pr-preview-environments",
          "articles/ci-cd-azure/local-testing",
          "articles/ci-cd-azure/tag-based-releases",
          "articles/ci-cd-azure/multi-stage-deployment",
        ],
      },
      {
        type: "category",
        label: "CircleCI",
        link: "articles/custom-ci-cd-circleci",
        items: [
          "articles/ci-cd-circleci/basic-deployment",
          "articles/ci-cd-circleci/deploy-and-test",
          "articles/ci-cd-circleci/pr-preview-environments",
          "articles/ci-cd-circleci/local-testing",
          "articles/ci-cd-circleci/tag-based-releases",
          "articles/ci-cd-circleci/multi-stage-deployment",
        ],
      },
      "articles/custom-ci-cd",
      "articles/monorepo-deployment",
      "articles/troubleshooting-deployments-and-git-sync",
      "articles/rename-or-move-project",
    ],
  },
  {
    type: "category",
    label: "Analytics",
    icon: "chart-line",
    items: [
      "analytics/overview",
      "analytics/access-and-entitlements",
      "analytics/shared-controls",
      {
        type: "category",
        label: "Sections",
        items: [
          "analytics/tabs/requests",
          "analytics/tabs/origins",
          "analytics/tabs/consumers",
          "analytics/tabs/agents",
          "analytics/tabs/mcp",
          "analytics/tabs/graphql",
        ],
      },
      {
        type: "category",
        label: "Reference",
        items: [
          "analytics/reference/metrics-glossary",
          "analytics/reference/url-parameters",
        ],
      },
    ],
  },
  {
    type: "category",
    label: "Observability",
    icon: "eye",
    items: [
      {
        type: "category",
        label: "Audit Logs",
        items: [
          "articles/audit-logging",
          "articles/audit-logs",
          "articles/custom-audit-logs",
        ],
      },
      {
        type: "category",
        label: "Logging",
        items: [
          "articles/logging",
          "articles/log-plugin-gcp",
          "articles/log-plugin-loki",
          "articles/log-plugin-vmware-log-insight",
          "articles/log-plugin-aws-cloudwatch",
          "articles/log-plugin-datadog",
          "articles/log-plugin-dynatrace",
          "articles/log-plugin-new-relic",
          "articles/log-plugin-splunk",
          "articles/log-plugin-sumo",
        ],
      },
      {
        type: "category",
        label: "Data & Security",
        items: [
          "articles/plugin-akamai-api-security",
          "articles/plugin-azure-blob",
          "articles/plugin-azure-event-hubs",
          "articles/plugin-hydrolix-traffic-peak",
        ],
      },
      "articles/metrics-plugins",
      "articles/opentelemetry",
      "articles/monitoring-your-gateway",
      {
        type: "category",
        label: "Guides",
        items: [
          "articles/custom-logging-example",
          "articles/log-request-response-data",
          "articles/archiving-requests-to-storage",
        ],
      },
    ],
  },
  {
    label: "Networking & Infrastructure",
    type: "category",
    icon: "cloud",
    items: [
      "articles/hosting-options",
      {
        type: "category",
        label: "Managed Dedicated",
        items: [
          "dedicated/overview",
          "dedicated/architecture",
          "dedicated/networking",
          "dedicated/custom-domains",
          "dedicated/source-control",
          "dedicated/federated-gateways",
          {
            type: "category",
            label: "Configuration",
            items: [
              "dedicated/configuration/overview",
              "dedicated/configuration/host-header-override",
            ],
          },
          {
            type: "category",
            label: "AWS",
            items: [
              "dedicated/aws/architecture",
              "dedicated/aws/private-networking",
            ],
          },
          {
            type: "category",
            label: "Azure",
            items: [
              "dedicated/azure/architecture",
              "dedicated/azure/private-networking",
            ],
          },
          {
            type: "category",
            label: "Google Cloud",
            items: [
              "dedicated/gcp/architecture",
              "dedicated/gcp/private-networking",
            ],
          },
          {
            type: "category",
            label: "Akamai",
            items: [
              "dedicated/akamai/architecture",
              "dedicated/akamai/cdn",
              "dedicated/akamai/caching",
              "dedicated/akamai/ai-powered-applications",
            ],
          },
        ],
      },
      {
        type: "doc",
        label: "Managed Edge",
        file: "managed-edge/overview",
      },
      {
        type: "category",
        label: "Self Hosted",
        items: [
          "self-hosted/overview",
          "self-hosted/requirements",
          "self-hosted/install",
          "self-hosted/upgrade",
          "self-hosted/troubleshooting",
        ],
      },
      {
        type: "category",
        label: "Custom Domains",
        items: [
          "articles/custom-domains",
          "articles/certificate-pinning",
          "articles/fastly-zuplo-host-setup",
        ],
      },
      {
        type: "category",
        label: "Securing Your Backend",
        items: [
          "articles/securing-your-backend",
          {
            type: "category",
            label: "IAM Authentication",
            items: [
              "articles/upstream-iam-auth",
              "articles/gke-with-upstream-auth-policy",
            ],
          },
          {
            type: "category",
            label: "mTLS",
            items: [
              "articles/securing-backend-mtls",
              "articles/connect-to-aws-alb-with-mtls",
            ],
          },
          {
            type: "category",
            label: "Secure Tunnel",
            items: [
              "articles/secure-tunnel",
              "articles/tunnel-setup",
              "articles/tunnel-services",
              "articles/tunnel-advanced",
              "articles/tunnel-troubleshooting",
            ],
          },
        ],
      },
      {
        type: "category",
        label: "Web Application Firewalls",
        items: [
          "articles/waf-ddos",
          "articles/waf-ddos-fastly",
          "articles/waf-ddos-akamai",
          "articles/waf-ddos-aws-waf-shield",
        ],
      },
      "articles/ddos-protection",
    ],
  },
  {
    type: "category",
    label: "Account Management",
    icon: "users",
    items: [
      {
        type: "category",
        label: "Members & Roles",
        items: [
          "articles/accounts/members-and-roles",
          "articles/accounts/roles-and-permissions",
          "articles/accounts/managing-account-members",
          "articles/accounts/managing-project-members",
          "articles/accounts/switching-between-accounts",
        ],
      },
      {
        type: "category",
        label: "Account Settings",
        items: [
          "articles/accounts/enterprise-sso",
          "articles/accounts/renew-sso-credentials",
          "articles/accounts/zuplo-api-keys",
          "articles/accounts/default-api-key",
          "articles/accounts/billing",
          "articles/accounts/delete-account",
          "articles/accounts/audit-logs",
        ],
      },
      {
        type: "category",
        label: "User Settings",
        items: [
          "articles/users/profile",
          "articles/users/multifactor-authentication",
        ],
      },
    ],
  },
  {
    type: "category",
    label: "Programming API",
    icon: "code",
    items: programming,
  },
  {
    type: "doc",
    file: "build-with-ai",
    label: "Build with AI",
    icon: "sparkles",
  },
  {
    type: "category",
    label: "Zuplo CLI",
    icon: "terminal",
    items: [
      "cli/overview",
      "cli/create-zuplo-api",
      "cli/authentication",
      "cli/global-options",
      "cli/connectivity",
      ...cliSidebar,
    ],
  },
  {
    type: "category",
    label: "Migration Guides",
    icon: "arrow-right-left",
    items: [
      ...gettingStarted("migrate"),
      "articles/migration-overview",
      "articles/migrate-from-kong",
      "articles/migrate-from-apigee",
      "articles/migrate-from-aws-api-gateway",
      "articles/migrate-from-azure-apim",
    ],
  },
  {
    type: "doc",
    file: "articles/limits",
  },
  {
    type: "doc",
    file: "articles/version-support-policy",
  },
  {
    type: "doc",
    file: "articles/security",
  },
  {
    type: "doc",
    file: "articles/support",
  },
  {
    type: "link",
    label: "Trust & Compliance",
    to: "https://trust.zuplo.com",
  },
  {
    type: "link",
    label: "Changelog",
    to: "https://zuplo.com/changelog",
  },
];
